import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { ColumnFlexBox, CenteredFlexBox } from '../../FlexBox';
import useTranslate from '../../../Hooks/useTranslate';
import { catalogueKey } from './surfaceKey';
import { valuePathShape } from '../../../Helpers/resolveValuePath';

/** What the groups the catalogue names are called, where there is a word for them. */
const SOURCE_LABELS = {
  customer: 'CUSTOMER',
  attribute: 'ATTRIBUTE',
  projected: 'PROJECTED',
};

/** The groups that lead, whatever else the catalogue turns out to hold. */
const LEADING_SOURCES = ['customer', 'attribute'];

/**
 * The groups the available keys are listed under, the known ones first.
 *
 * The catalogue names a group for every class the customer is composed of, so which groups there
 * are is the catalogue's to say rather than something to keep a list of here.
 */
const sourceGroups = (available) => {
  const found = [...new Set(available.map(({ source }) => source))];
  const rest = found
    .filter((source) => !LEADING_SOURCES.includes(source))
    .sort();

  return [...LEADING_SOURCES, ...rest, 'projected']
    .filter((source) => found.includes(source))
    .map((source) => ({
      source,
      label: SOURCE_LABELS[source] ?? source.toUpperCase(),
      entries: available.filter((entry) => entry.source === source),
    }));
};

/**
 * Keys the checked-in layout declares that the catalogue does not hold, as catalogue entries.
 *
 * The catalogue reflects the entity, while a list procedure may project computed columns like
 * `fullNameSearch` - offerable because the shipped layout vouches for them.
 */
const projectedEntries = (catalogue, defaultLayout, surface) => {
  const known = new Set((catalogue ?? []).map((entry) => entry.key));

  return (defaultLayout ?? [])
    .filter((entry) => !known.has(catalogueKey(entry.key, surface)))
    .map((entry) => ({
      key: catalogueKey(entry.key, surface),
      label: entry.label ?? entry.key,
      type: 'text',
      source: 'projected',
    }));
};

/**
 * The keys not already taken, by source.
 *
 * Taken is judged on the shape of a key rather than on the key itself: a property read at one index
 * of a collection and at another is the same property, and offering it again would say otherwise.
 */
const availableEntries = (catalogue, entries, surface, defaultLayout) => {
  const chosen = new Set(
    (entries ?? []).map((entry) =>
      valuePathShape(catalogueKey(entry.key, surface))
    )
  );

  return sourceGroups(
    [
      ...(catalogue ?? []),
      ...projectedEntries(catalogue, defaultLayout, surface),
    ].filter((entry) => !chosen.has(valuePathShape(entry.key)))
  ).filter((group) => group.entries.length > 0);
};

/**
 * The keys a surface can still show, to pick one and add it.
 *
 * Grouped by where the key comes from and searchable, since a section's catalogue runs to hundreds
 * of attributes.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.catalogue - What the section can show.
 * @param {Array} props.entries - What it already shows, so those keys are not offered twice.
 * @param {String} props.surface - The surface whose dialect the entries' keys are in.
 * @param {Array} props.defaultLayout - The layout checked into the client, for the keys it projects.
 * @param {Function} props.onPick - Receives the catalogue entry chosen.
 *
 * @returns {React.JSX.Element}
 */
const KeyPicker = function KeyPicker({
  catalogue = [],
  entries = [],
  surface = 'list',
  defaultLayout = [],
  onPick = () => {},
}) {
  const intl = useIntl();
  const translate = useTranslate();
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState({
    customer: true,
    attribute: true,
    projected: true,
  });

  const toggleGroup = (source) =>
    setOpenGroups((current) => ({ ...current, [source]: !current[source] }));

  // While searching every group is open, or a match inside a collapsed one would be unreachable.
  const isGroupOpen = (source) =>
    Boolean(search) || Boolean(openGroups[source]);

  const available = useMemo(() => {
    const groups = availableEntries(catalogue, entries, surface, defaultLayout);
    const query = search.trim().toLowerCase();

    if (!query) return groups;

    return groups
      .map((group) => ({
        ...group,
        entries: group.entries.filter(
          ({ key, label }) =>
            key?.toLowerCase().includes(query) ||
            String(intl.messages[label] ?? label)
              .toLowerCase()
              .includes(query)
        ),
      }))
      .filter((group) => group.entries.length > 0);
  }, [catalogue, entries, surface, defaultLayout, search, intl.messages]);

  return (
    <ColumnFlexBox sx={{ flex: 1, minHeight: 0 }} gap="8px">
      <TextField
        id="filter-keys-input"
        size="small"
        placeholder={intl.messages.SEARCH}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  id="clear-key-search-btn"
                  size="small"
                  onClick={() => setSearch('')}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
        sx={{ flex: 'none' }}
      />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: '4px' }}>
        <List dense disablePadding>
          {available.map((group) => (
            <React.Fragment key={group.source}>
              <ListItemButton
                onClick={() => toggleGroup(group.source)}
                sx={{ borderRadius: '8px' }}
              >
                <ListItemText
                  primary={`${translate(group.label)} (${group.entries.length})`}
                  slotProps={{ primary: { sx: { fontWeight: 600 } } }}
                />
                <KeyboardArrowDownRoundedIcon
                  sx={{
                    transform: isGroupOpen(group.source)
                      ? 'rotate(180deg)'
                      : 'none',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </ListItemButton>
              <Collapse in={isGroupOpen(group.source)} unmountOnExit>
                {group.entries.map((entry) => (
                  <ListItemButton
                    key={entry.key}
                    onClick={() => onPick(entry)}
                    sx={{ borderRadius: '8px', pl: '24px' }}
                  >
                    <ListItemText
                      primary={
                        <CenteredFlexBox gap="6px">
                          {translate(entry.label)}
                          {entry.missingIn?.length > 0 && (
                            <Tooltip
                              arrow
                              title={`${intl.messages.DECLARED_IN}: ${entry.declaredIn?.join(
                                ', '
                              )} — ${intl.messages.MISSING_IN}: ${entry.missingIn?.join(', ')}`}
                            >
                              <InfoOutlinedIcon
                                fontSize="small"
                                sx={{ color: 'info.main' }}
                              />
                            </Tooltip>
                          )}
                          {entry.conflicts && (
                            <Tooltip
                              arrow
                              title={intl.messages.ATTRIBUTE_CONFLICT}
                            >
                              <WarningAmberRoundedIcon
                                fontSize="small"
                                sx={{ color: 'warning.main' }}
                              />
                            </Tooltip>
                          )}
                        </CenteredFlexBox>
                      }
                      secondary={entry.key}
                    />
                  </ListItemButton>
                ))}
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Box>
    </ColumnFlexBox>
  );
};

export default KeyPicker;
